import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { USER_ROLE_ORDER, UserRole } from '@examdojo/models/user';
import { ToastService } from '@examdojo/toast';
import { UserQuery, UserService } from '@examdojo/user';
import { catchError, of, switchMap, take } from 'rxjs';
import { DEFAULT_ROOT_URL_PART } from '../../app.model';

export function getUserRoleGuard(role: UserRole, redirectTo: string = DEFAULT_ROOT_URL_PART): CanActivateFn {
  return () => {
    const toastService = inject(ToastService);
    const router = inject(Router);
    const userQuery = inject(UserQuery);
    const userService = inject(UserService);

    // Check if role data is already available in store
    try {
      const hasPermission = userQuery.hasRoleOrHigher(role);
      if (hasPermission) {
        return true; // Allow access immediately
      } else {
        // Deny access immediately if role is insufficient
        toastService.warning('You are not authorized to access this page');
        return router.parseUrl(redirectTo);
      }
    } catch (error) {
      // Role data not available, need to fetch it
    }

    // Fetch role data and use the fetched result directly
    return userService.fetchCurrentUserRole().pipe(
      take(1),
      switchMap((fetchedRole) => {
        // Use fetched role directly instead of querying store again
        if (!fetchedRole) {
          toastService.error('Unable to verify permissions. Please try again.');
          return of(router.parseUrl(redirectTo));
        }

        // Check permission using fetched role directly
        const currentRoleIndex = USER_ROLE_ORDER.indexOf(fetchedRole);
        const requiredRoleIndex = USER_ROLE_ORDER.indexOf(role);
        const hasPermission = currentRoleIndex <= requiredRoleIndex;

        if (!hasPermission) {
          toastService.warning('You are not authorized to access this page');
          return of(router.parseUrl(redirectTo));
        }

        return of(true);
      }),
      catchError((error: unknown) => {
        console.warn('[getUserRoleGuard]: Failed to fetch user role', error);
        toastService.error('Unable to verify permissions. Please try again.');
        return of(router.parseUrl(redirectTo));
      }),
    );
  };
}
